import React from 'react';
import Modal from 'components/shared/DesignSystem/Modal';
import styled from 'styled-components';
import UpgradeBadge from 'components/shared/DesignSystem/UpgradeBadge';
import ValuePropsContent, {
  ValuePropsContentProps,
} from 'components/paywall/ValuePropsContent';
import { useCurrentPermissions } from 'javascripts/hooks';
import Steven from 'helpers/steven';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_PREMIUM_SAMPLE_FOR_EXISTING } from 'constants/featureFlags';
import { usePremiumSampleEligible } from '../billing/v2/trials/hooks';
import { Button } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';

// #region styled-components
const ModalDiv = styled.div`
  display: flex;
  align-items: flex-end;
`;

const CopyDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  margin: 64px 64px 40px 64px;
`;

const UpgradeForAccessContainer = styled.div`
  display: flex;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
`;

const ImageDiv = styled.div<{ image: string }>`
  display: flex;
  background-image: url(${(props) => props.image});
  background-size: contain;
  background-position: 100%;
  background-repeat: no-repeat;
  width: 350px;
  height: 100%;
  margin-left: var(--cc_size_spacing_xl);

  @media (max-width: 850px) {
    display: none;
  }
`;
// #endregion

type ValuePropsModalProps = Pick<
  ValuePropsContentProps,
  'title' | 'description'
> & {
  image: string;
  checklistItems: ValuePropsContentProps['items'];
  onRequestClose: () => void;
  trackingAction?: string;
  dataTestId?: string;
};

const ValuePropsModal = ({
  title,
  image,
  description,
  checklistItems,
  onRequestClose,
  trackingAction,
  dataTestId,
}: ValuePropsModalProps) => {
  const { canManageBilling } = useCurrentPermissions();
  const flags = useFlags();
  const { isPremiumSampleEligible } = usePremiumSampleEligible();
  const isPremiumSampleEnabled = flags[LD_PREMIUM_SAMPLE_FOR_EXISTING];

  const logClickEvent = () => {
    if (trackingAction) {
      Steven.elementClicked({
        id: 'value_prop_modal_upgrade',
        content: 'Upgrade',
        type: 'link',
        action: trackingAction,
      });
    }
  };

  const renderButton = () => {
    if (isPremiumSampleEligible && isPremiumSampleEnabled) {
      return (
        <Button color="primary" href="/billing" onClick={logClickEvent}>
          {translate('valueProps.shared.freePremiumSample')}
        </Button>
      );
    } else if (canManageBilling) {
      return (
        <Button
          data-testid="value-props-modal__upgrade-button"
          color="primary"
          href="/billing"
          onClick={logClickEvent}
        >
          {translate('valueProps.shared.upgradeToPro')}
        </Button>
      );
    } else {
      return (
        <Button color="primary" onClick={onRequestClose}>
          {translate('valueProps.shared.dismiss')}
        </Button>
      );
    }
  };

  return (
    <Modal
      isOpen
      isClosable
      onRequestClose={onRequestClose}
      className="modal-content-wrap pro-feature-modal"
    >
      <ModalDiv
        data-testid={
          dataTestId ? `value_props_modal__${dataTestId}` : undefined
        }
      >
        <ImageDiv image={image} />
        <CopyDiv>
          <UpgradeForAccessContainer>
            <UpgradeBadge />
          </UpgradeForAccessContainer>
          <ValuePropsContent
            title={title}
            description={description}
            items={checklistItems}
          />
          <ButtonContainer>{renderButton()}</ButtonContainer>
        </CopyDiv>
      </ModalDiv>
    </Modal>
  );
};

export default ValuePropsModal;
